import { useState } from 'react'

import { Avatar } from '@nextui-org/avatar'
import { Divider, Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from '@nextui-org/react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useLogout } from '@privy-io/react-auth'

import ICONS from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { authActions } from '@/redux/reducers/auth/auth.reducer'
import { settingsActions } from '@/redux/reducers/settings/setting.reducer'
import useIsMobile from '@/hooks/useIsMobile'
// import { SCORE } from '@/utils'
import SVGS from '@/assets/svgs'
import { formatNumber } from '@/utils/formatNumber'

const UserBottomSidebar: React.FC = () => {
  const isMobile = useIsMobile()
  const [isShowBalance, setIsShowBalance] = useState<boolean>(false)
  const spaces = useAppSelector((state) => state.root.auth.user?.spaces)
  const spaceActive = useAppSelector((state) => state.root.settings.spaceActive)

  const router = useRouter()
  const dispatch = useAppDispatch()
  const { logout: handleLogout } = useLogout({
    onSuccess: () => {
      dispatch(authActions.logout())
      dispatch(settingsActions.setActiveSpace(''))
      router.push('/sign-in')
    },
  })

  const user = useAppSelector((state) => state.root.auth.user)
  // const chain = useActiveChainId()
  // const ercAddress = ADDRESS_CONTRACT_BY_CHAIN[chain.chainId].USDT_ADDRESS
  // const { balanceOf, symbol } = useERC20(ercAddress)

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="border border-white/10 space-y-2 py-3 px-4 rounded-2xl">
        <div className="flex items-center gap-x-2">
          <p className="text-sm text-ss-grayscale-500">SightSea Point (SSP)</p>
          <Image
            onClick={() => setIsShowBalance((prev) => !prev)}
            alt=""
            src={!isShowBalance ? SVGS.iconHiddenEye : ICONS.eyes}
            className="h-[14px] w-[14px] cursor-pointer"
          />
        </div>
        <div className="flex items-center gap-2">
          <Image alt="" src={ICONS.ssPoint} className="h-4 w-4" />
          {/* <Image alt="" src={ICONS.crypto} className="h-[14px] w-[14px]" /> */}
          <div className="flex-1 flex gap-1 flex-wrap">
            <div className="text-ss-green-200  text-base font-medium flex-1 truncate">
              {isShowBalance ? formatNumber(user?.questPoint || 0) : '**********'} SSP
            </div>

            {/* <div className=" ">
              <span className="text-base font-medium text-ss-grayscale-300"> ≈ </span>
              <span className="text-sm text-ss-grayscale-300">
                {isShowBalance ? formatNumber((user?.questPoint || 0) * SCORE) : '****'}
              </span>
              <span className="text-sm text-ss-grayscale-500"> USDT</span>
            </div> */}
          </div>
        </div>
      </div>
      {!isMobile && (
        <>
          <Divider />
          <div className="flex items-center gap-2">
            <Avatar
              className="h-[38px] w-[38px] shrink-0"
              src={spaceActive ? user?.spaceAvatarUrl : user?.avatarImage}
            />
            <div className="flex-1 shrink min-w-0 flex gap-1">
              <h3 className="text-sm font-semibold text-ss-grayscale-100 truncate min-w-0">
                {spaceActive ? user?.spaceNickname : user?.nickname}
              </h3>

              {spaceActive ? (
                <Image src={ICONS.icVerifySpace} width={14} height={14} alt="Verify" />
              ) : user?.role?.value === 'kol' ? (
                <Image src={ICONS.verifyBadge} width={14} height={14} alt="Verify" />
              ) : null}
            </div>

            <Dropdown className="border border-white/10 bg-ss-dark">
              <DropdownTrigger>
                <Image src={ICONS.threeDotVertical} className="ml-auto h-[20px] w-[20px] cursor-pointer" alt="" />
              </DropdownTrigger>
              <DropdownMenu variant="faded" aria-label="Dropdown menu with icons">
                <DropdownSection showDivider className="mb-2">
                  {spaces?.length
                    ? spaces.map((item) => {
                        if (spaceActive === item.id) return
                        return (
                          <DropdownItem
                            closeOnSelect={false}
                            className="text-white hover:border-white/10 data-[hover=true]:bg-ss-grayscale-800 py-2.5 px-3 data-[hover=true]:text-white mb-2"
                            key={`space-${item.id}`}
                          >
                            <div
                              key={item.id}
                              className="flex items-center gap-2"
                              onClick={() => {
                                dispatch(settingsActions.setActiveSpace(item.id))
                                router.push(`/my-space/${item.id}?tab=campaigns`)
                              }}
                            >
                              <Avatar
                                alt={item?.profileImageUrl}
                                className="flex-shrink-0"
                                size="sm"
                                src={item?.profileImageUrl}
                              />
                              <div className="text-white flex gap-1 items-center">
                                <span>{item?.name}</span>
                                <Image src={ICONS.icVerifySpace} width={14} height={14} alt="Verify" />
                              </div>
                            </div>
                          </DropdownItem>
                        )
                      })
                    : null}
                  {spaceActive && (
                    <DropdownItem
                      className="text-white hover:border-white/10 data-[hover=true]:bg-ss-grayscale-800 py-2.5 px-3 data-[hover=true]:text-white mb-2"
                      key={'my-profile'}
                    >
                      <div
                        className="flex gap-2 items-center cursor-pointer"
                        onClick={() => {
                          dispatch(settingsActions.setActiveSpace(''))
                          router.push('/profile')
                        }}
                      >
                        <Avatar
                          alt={user?.nicknameOrigin}
                          className="flex-shrink-0"
                          size="sm"
                          src={user?.avatarOrigin}
                        />
                        <div className="flex items-center gap-1">
                          <span className="text-small text-white">{user?.nicknameOrigin}</span>
                          {user?.role?.value === 'kol' && (
                            <Image src={ICONS.verifyBadge} width={14} height={14} alt="Verify" />
                          )}
                        </div>
                      </div>
                    </DropdownItem>
                  )}

                  <DropdownItem
                    onPress={() => router.push('/spaces/create')}
                    className="text-white hover:border-white/10 data-[hover=true]:bg-ss-grayscale-800 bg-ss-grayscale-800 py-2.5 px-3 data-[hover=true]:text-white mb-2"
                    key="copy"
                    startContent={<Image src={ICONS.plusCircleWhite} alt="" />}
                  >
                    Create new Space
                  </DropdownItem>
                </DropdownSection>
                <DropdownItem
                  onPress={() => {
                    handleLogout()
                  }}
                  className="text-white hover:border-white/10 data-[hover=true]:bg-ss-dark data-[hover=true]:text-white py-2.5 px-3"
                  key="copy"
                  startContent={<Image src={ICONS.signOut} alt="" />}
                >
                  Log out
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </>
      )}
    </div>
  )
}

export default UserBottomSidebar
